import { useState, useMemo } from 'react'
import { useQuery, useMutation } from '@apollo/client'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  CircularProgress,
  Chip,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material'
import { HourglassEmpty } from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { Navigate, useOutletContext } from 'react-router-dom'

import { Button, CardBox, Container, SelfAdvisorConfirmDialog, Stack, Text } from '../../components'
import { NotificationDialog } from '../../components/dialog/notification-dialog'
import { queries, mutations } from '../../graphql'
import { useAuth } from '../../hooks'
import { useBranding } from '../../hooks/useBranding'

// Must stay in sync with DEFAULT_ADVISOR_PERMISSIONS in api/src/advisor-permissions/constants.ts
const DEFAULT_PERMISSIONS = {
  menuGivingActivity: true,
  menuInvestmentActivity: true,
  menuContribute: true,
  menuDocuments: true,
  menuGrants: false,
}

// Status constants matching OnboardingFlowStatus enum
const FLOW_STATUS = {
  COMPLETED: 4,
  EXPIRED: 5,
  CANCELLED: 6,
}

function stripTypename({ __typename, ...rest }) {
  return rest
}

function getInviteStatusInfo(invite) {
  const now = new Date()
  const isExpired = invite.expiresAt && new Date(invite.expiresAt) < now
  const { status } = invite

  if (status === FLOW_STATUS.COMPLETED) return { label: 'Accepted', color: 'success' }
  if (status === FLOW_STATUS.CANCELLED) return { label: 'Cancelled', color: 'default' }
  if (status === FLOW_STATUS.EXPIRED || isExpired) return { label: 'Expired', color: 'warning' }
  return { label: 'Pending', color: 'info' }
}

function InviteAdvisorDialog({ open, onClose, fundId, onInvited }) {
  const [advisorName, setAdvisorName] = useState('')
  const [advisorEmail, setAdvisorEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [confirmOpen, setConfirmOpen] = useState(false)
  const { data: { me } = {} } = useQuery(queries.user.me)

  const [inviteAdvisor, { loading }] = useMutation(mutations.onboarding.donorInviteAdvisor, {
    onCompleted: (data) => {
      if (data?.donorInviteAdvisor?.success) {
        setAdvisorName('')
        setAdvisorEmail('')
        setErrorMessage('')
        onClose()
        onInvited?.()
      }
    },
    onError: (error) => {
      setErrorMessage(error.message || 'Failed to send invitation. Please try again.')
    },
  })

  const sendInvite = () => {
    setConfirmOpen(false)
    inviteAdvisor({
      variables: {
        input: { advisorEmail, advisorName, fundId },
      },
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setErrorMessage('')
    // A donor can be their own advisor, but make them confirm the match is intended
    if (me?.email && advisorEmail.trim().toLowerCase() === me.email.trim().toLowerCase()) {
      setConfirmOpen(true)
      return
    }
    sendInvite()
  }

  const handleClose = () => {
    setAdvisorName('')
    setAdvisorEmail('')
    setErrorMessage('')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Invite Financial Advisor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Your financial advisor will start with limited access. You can adjust what they can view and do after they
            accept.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Default access includes:
          </Typography>
          <Box component="ul" sx={{ mt: 0, mb: 1, pl: 3 }}>
            <Typography component="li" variant="body2" color="text.secondary">
              View Activity
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              View Tax Receipts
            </Typography>
            <Typography component="li" variant="body2" color="text.secondary">
              Add Funds
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            (Send Gifts not included by default)
          </Typography>
          <TextField
            autoFocus
            label="Financial Advisor Name (Optional)"
            fullWidth
            value={advisorName}
            onChange={(e) => setAdvisorName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Financial Advisor Email"
            type="email"
            fullWidth
            required
            value={advisorEmail}
            onChange={(e) => setAdvisorEmail(e.target.value)}
          />
          {errorMessage && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !advisorEmail.trim()}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loading ? 'Sending...' : 'Send Invitation'}
          </Button>
          <Button onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
        </DialogActions>
      </form>
      <SelfAdvisorConfirmDialog
        open={confirmOpen}
        subject="the logged in user"
        onBack={() => setConfirmOpen(false)}
        onInvite={sendInvite}
        loading={loading}
      />
    </Dialog>
  )
}

const SOURCE_LABELS = {
  admin_advisor_invite: 'Invited by admin',
  securities_advisor: 'Securities contribution',
}

// FLIGHT-546: donors sometimes paste the rep code into the Advisor Name field
// on the securities form, leaving the dedicated Dealer Rep Code field empty.
// Parse it back out so the card heading shows the name only and the rep code
// gets its own labelled line. Forward-compatible: if the structured
// advisorDealerRepCode field is set, that wins.
function parseAdvisorName(rawName) {
  if (!rawName) return { name: '', repCode: null }
  const match = rawName.match(/\s*[-,|]?\s*Rep\s*code\s*[:-]?\s*(.+?)\s*$/i)
  if (match) {
    return {
      name: rawName.slice(0, match.index).trim(),
      repCode: match[1].trim(),
    }
  }
  return { name: rawName.trim(), repCode: null }
}

function PendingInviteCard({ invite, onAction }) {
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const { accentColor, accentTextColor } = useBranding()
  const metadata = invite.metadata || {}
  const advisorEmail = invite.targetEmail || metadata.advisorEmail || ''
  const parsedFromName = parseAdvisorName(metadata.advisorName)
  const advisorName = parsedFromName.name || advisorEmail || 'Unknown'
  const dealerRepCode = metadata.advisorDealerRepCode || parsedFromName.repCode || null
  const statusInfo = getInviteStatusInfo(invite)
  const isPending = statusInfo.label === 'Pending'
  const isExpired = statusInfo.label === 'Expired'
  const isDonorInvite = invite.flowType === 'donor_advisor_invite'
  const isSecurities = invite.flowType === 'securities_advisor'
  const sourceLabel = SOURCE_LABELS[invite.flowType]

  const [resendInvite, { loading: resending }] = useMutation(mutations.onboarding.donorResendAdvisorInvite, {
    onCompleted: () => {
      setErrorMessage('')
      onAction?.()
    },
    onError: (err) => setErrorMessage(err.message || 'Failed to resend invitation.'),
  })

  const [cancelInvite, { loading: cancelling }] = useMutation(mutations.onboarding.donorCancelAdvisorInvite, {
    onCompleted: () => {
      setConfirmCancelOpen(false)
      onAction?.()
    },
    onError: (err) => {
      setConfirmCancelOpen(false)
      setErrorMessage(err.message || 'Failed to cancel invitation.')
    },
  })

  const [reissueInvite, { loading: reissuing }] = useMutation(mutations.onboarding.donorReissueAdvisorInvite, {
    onCompleted: () => {
      setErrorMessage('')
      onAction?.()
    },
    onError: (err) => setErrorMessage(err.message || 'Failed to reissue invitation.'),
  })

  return (
    <Card variant="outlined" sx={{ mb: 2, width: '50%', boxShadow: 1, borderColor: 'grey.400' }}>
      <CardContent>
        <Box sx={{ mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">{advisorName}</Typography>
            <Tooltip
              title={
                isExpired
                  ? 'This invitation has expired'
                  : `Sent ${formatDistanceToNow(new Date(invite.createdAt), { addSuffix: true })}`
              }
            >
              <Chip
                label={isExpired ? 'Expired' : 'Pending Acceptance'}
                size="small"
                icon={<HourglassEmpty fontSize="small" />}
                sx={
                  isExpired
                    ? { backgroundColor: 'error.main', color: 'error.contrastText' }
                    : { backgroundColor: accentColor, color: accentTextColor }
                }
              />
            </Tooltip>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Financial Advisor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {advisorEmail}
          </Typography>
          {dealerRepCode && (
            <Typography variant="body2" color="text.secondary">
              Dealer Rep Code: {dealerRepCode}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="body2" color="text.secondary">
          Sent {new Date(invite.createdAt).toLocaleDateString()}
          {invite.expiresAt && ` | Expires ${new Date(invite.expiresAt).toLocaleDateString()}`}
        </Typography>
        {sourceLabel && (
          <Typography variant="body2" color="text.secondary">
            {sourceLabel}
          </Typography>
        )}

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
          {isPending && (
            <>
              {isDonorInvite && (
                <Button
                  variant="outlined"
                  onClick={() => resendInvite({ variables: { flowId: invite.id } })}
                  disabled={resending}
                >
                  {resending ? 'Resending...' : 'Resend'}
                </Button>
              )}
              <Button variant="outlined" color="error" onClick={() => setConfirmCancelOpen(true)} disabled={cancelling}>
                Cancel Invitation
              </Button>
            </>
          )}
          {isExpired && (
            <>
              {isDonorInvite && (
                <Button
                  variant="outlined"
                  onClick={() => reissueInvite({ variables: { flowId: invite.id } })}
                  disabled={reissuing}
                >
                  {reissuing ? 'Sending...' : 'Resend Invite'}
                </Button>
              )}
              <Button variant="outlined" color="error" onClick={() => setConfirmCancelOpen(true)} disabled={cancelling}>
                Delete
              </Button>
            </>
          )}
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}
      </CardContent>

      <NotificationDialog
        open={confirmCancelOpen}
        onClose={() => setConfirmCancelOpen(false)}
        type="alert"
        title={isExpired ? 'Delete Invitation' : 'Cancel Invitation'}
        message={(() => {
          if (isSecurities)
            return `Are you sure you want to ${
              isExpired ? 'delete the expired' : 'cancel the'
            } invitation to ${advisorName}? This will also cancel the linked securities contribution${
              metadata.amount ? ` of ${metadata.amount}` : ''
            }.`
          if (isExpired) return `Are you sure you want to delete the expired invitation to ${advisorName}?`
          return `Are you sure you want to cancel the invitation to ${advisorName}?`
        })()}
        actions={[
          { label: 'Keep', onClick: () => setConfirmCancelOpen(false) },
          {
            label: isExpired ? 'Delete' : 'Cancel Invitation',
            onClick: () => cancelInvite({ variables: { flowId: invite.id } }),
            variant: 'contained',
            color: 'error',
            disabled: cancelling,
          },
        ]}
      />
    </Card>
  )
}

function AdvisorCard({ advisor, fundId, onSaved, onRemoved }) {
  const [localPermissions, setLocalPermissions] = useState(() => stripTypename(advisor.permissions))
  const [hasChanges, setHasChanges] = useState(false)
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false)
  const [successMessage, setSuccessMessage] = useState('')
  const [errorMessage, setErrorMessage] = useState('')

  const [updatePermissions, { loading: saving }] = useMutation(mutations.advisorPermissions.updateAdvisorPermissions, {
    onCompleted: () => {
      setHasChanges(false)
      setErrorMessage('')
      setSuccessMessage('Permissions updated successfully.')
      onSaved?.()
    },
    onError: () => {
      setErrorMessage('Failed to update permissions. Please try again.')
    },
  })

  const [removeAdvisor, { loading: removing }] = useMutation(mutations.advisorPermissions.removeAdvisorFromFund, {
    onCompleted: () => {
      setConfirmRemoveOpen(false)
      onRemoved?.()
    },
    onError: () => {
      setConfirmRemoveOpen(false)
      setErrorMessage('Failed to remove advisor. Please try again.')
    },
  })

  const handleToggle = (flag) => {
    const updated = { ...localPermissions, [flag]: !localPermissions[flag] }
    setLocalPermissions(updated)
    setHasChanges(true)
    setSuccessMessage('')
    setErrorMessage('')
  }

  const handleResetDefaults = () => {
    setLocalPermissions({ ...DEFAULT_PERMISSIONS })
    setHasChanges(true)
    setSuccessMessage('')
    setErrorMessage('')
  }

  const handleSave = () => {
    updatePermissions({
      variables: {
        input: {
          advisorId: advisor.advisorId,
          fundId,
          ...localPermissions,
        },
      },
    })
  }

  const handleRemove = () => {
    removeAdvisor({
      variables: {
        input: {
          advisorId: advisor.advisorId,
          fundId,
        },
      },
    })
  }

  const advisorName =
    (advisor.isOrganization && advisor.corporateName) ||
    [advisor.firstName, advisor.lastName].filter(Boolean).join(' ') ||
    advisor.email

  return (
    <Card variant="outlined" sx={{ mb: 2, width: '50%', boxShadow: 1, borderColor: 'grey.400' }}>
      <CardContent>
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6">{advisorName}</Typography>
          <Typography variant="body2" color="text.secondary">
            Financial Advisor
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {advisor.email}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Permissions
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Switch
              checked={!!localPermissions.menuGivingActivity}
              onChange={() => handleToggle('menuGivingActivity')}
              disabled={saving}
            />
            <Box sx={{ ml: 1 }}>
              <Typography variant="body2">View Giving Wallet Activity</Typography>
              <Typography variant="caption" color="text.secondary">
                See contributions, gifts, and balances in the Giving Wallet
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Switch
              checked={!!localPermissions.menuInvestmentActivity}
              onChange={() => handleToggle('menuInvestmentActivity')}
              disabled={saving}
            />
            <Box sx={{ ml: 1 }}>
              <Typography variant="body2">View Investment Account Activity</Typography>
              <Typography variant="caption" color="text.secondary">
                See contributions and balances in the Investment Account
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Switch
              checked={!!localPermissions.menuDocuments}
              onChange={() => handleToggle('menuDocuments')}
              disabled={saving}
            />
            <Box sx={{ ml: 1 }}>
              <Typography variant="body2">View Tax Receipts</Typography>
              <Typography variant="caption" color="text.secondary">
                Download contribution receipts
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Switch
              checked={!!localPermissions.menuContribute}
              onChange={() => handleToggle('menuContribute')}
              disabled={saving}
            />
            <Box sx={{ ml: 1 }}>
              <Typography variant="body2">Add Funds</Typography>
              <Typography variant="caption" color="text.secondary">
                Make contributions to the fund
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
            <Switch
              checked={!!localPermissions.menuGrants}
              onChange={() => handleToggle('menuGrants')}
              disabled={saving}
            />
            <Box sx={{ ml: 1 }}>
              <Typography variant="body2">Send Gifts</Typography>
              <Typography variant="caption" color="text.secondary">
                Recommend and send gifts to charities
              </Typography>
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Button variant="outlined" onClick={handleResetDefaults} disabled={saving}>
            Reset Defaults
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={!hasChanges || saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>

          <Button variant="outlined" color="error" onClick={() => setConfirmRemoveOpen(true)} disabled={removing}>
            Remove Advisor
          </Button>
        </Box>

        {successMessage && (
          <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}
        {errorMessage && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErrorMessage('')}>
            {errorMessage}
          </Alert>
        )}
      </CardContent>

      <NotificationDialog
        open={confirmRemoveOpen}
        onClose={() => setConfirmRemoveOpen(false)}
        type="alert"
        title="Remove Advisor"
        message={`Are you sure you want to remove ${advisorName} from this fund? They will no longer be able to view or manage this fund.`}
        actions={[
          { label: 'Cancel', onClick: () => setConfirmRemoveOpen(false) },
          { label: 'Remove', onClick: handleRemove, variant: 'contained', color: 'error', disabled: removing },
        ]}
      />
    </Card>
  )
}

function FundAdvisorsSection({ fund, invites, onInvitesRefetch }) {
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const { data, loading, refetch } = useQuery(queries.advisorPermissions.fundAdvisors, {
    variables: { fundId: fund.id },
    fetchPolicy: 'cache-and-network',
  })

  const advisors = data?.fundAdvisors || []

  const thirtyDaysAgo = useMemo(() => {
    const d = new Date()
    d.setDate(d.getDate() - 30)
    return d
  }, [])

  const pendingInvites = invites.filter((inv) => {
    if (inv.fundId !== fund.id) return false
    if (inv.status === FLOW_STATUS.COMPLETED || inv.status === FLOW_STATUS.CANCELLED) return false
    if (inv.status === FLOW_STATUS.EXPIRED && new Date(inv.updatedAt) < thirtyDaysAgo) return false
    return true
  })

  const combined = [
    ...advisors.map((a) => ({ type: 'advisor', data: a, createdAt: new Date(a.createdAt) })),
    ...pendingInvites.map((i) => ({ type: 'invite', data: i, createdAt: new Date(i.createdAt) })),
  ].sort((a, b) => b.createdAt - a.createdAt)

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">{fund.name}</Typography>
        <Button variant="contained" size="small" onClick={() => setInviteDialogOpen(true)}>
          Invite Financial Advisor
        </Button>
      </Box>

      <Box>
        <Typography variant="subtitle1" gutterBottom>
          Financial Advisors
        </Typography>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {!loading && combined.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            No advisors connected to this fund yet.
          </Typography>
        )}
        {!loading &&
          combined.map((item) =>
            item.type === 'advisor' ? (
              <AdvisorCard
                key={`advisor-${item.data.advisorId}`}
                advisor={item.data}
                fundId={fund.id}
                onSaved={() => refetch()}
                onRemoved={() => refetch()}
              />
            ) : (
              <PendingInviteCard key={`invite-${item.data.id}`} invite={item.data} onAction={onInvitesRefetch} />
            )
          )}
      </Box>

      <InviteAdvisorDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        fundId={fund.id}
        onInvited={onInvitesRefetch}
      />
    </Box>
  )
}

export function AdvisorManagement() {
  const [{ impersonationType }] = useAuth()
  const { donorFundId } = useOutletContext() ?? {}
  const { data: fundData, loading: fundLoading } = useQuery(queries.funds.myFund, {
    variables: { fundId: donorFundId },
    skip: !donorFundId,
  })
  const fund = fundData?.me?.fund
  const isAdvisorImpersonation = impersonationType === 'ADVISOR'

  const { data: invitesData, refetch: refetchInvites } = useQuery(queries.onboarding.myDonorAdvisorInvites, {
    skip: isAdvisorImpersonation,
    fetchPolicy: 'cache-and-network',
  })
  const invites = invitesData?.myDonorAdvisorInvites || []

  // Block direct URL access during advisor impersonation
  if (isAdvisorImpersonation) {
    return <Navigate to="/profile/personal-details" replace />
  }

  if (fundLoading || !fund) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Container>
    )
  }

  return (
    <Stack>
      <CardBox>
        <Text.Body color="text.secondary" sx={{ mb: 2 }}>
          Manage your financial advisor&apos;s access to this Giving Fund. You can control what they can view and what
          actions they can take.
        </Text.Body>
        <FundAdvisorsSection fund={fund} invites={invites} onInvitesRefetch={() => refetchInvites()} />
      </CardBox>
    </Stack>
  )
}
